import { motion } from "framer-motion";
import { Award, Target, Eye, CheckCircle, Zap, Shield, Rocket } from "lucide-react";
import { WeatherEffect } from "@/components/ui/rain-and-lightening-hero-section";
import { useEffect } from "react";

// --- CUSTOM CSS FOR ELECTRIC THEME ---
const customStyles = `
  .text-glow {
    text-shadow: 0 0 15px, 0 0 30px rgba(168, 85, 247, 0.3);
  }
  .glass-panel {
    background: rgba(2, 6, 23, 0.4); /* More transparent for rain visibility */
    backdrop-filter: blur(8px);       /* Reduced blur to see rain movement */
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  }
  .electric-card {
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  .electric-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
  .electric-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px -10px rgba(6, 182, 212, 0.3);
    border-color: rgba(6, 182, 212, 0.5);
  }
`;

const About = () => {
  useEffect(() => {
    if (window.location.hash === '#thunder-fox-about') {
      const element = document.getElementById('thunder-fox-about');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  return (
    <main id="thunder-fox-about" className="relative min-h-screen bg-transparent text-white selection:bg-cyan-500 selection:text-black overflow-hidden font-sans">
      <style>{customStyles}</style>

      {/* --- BACKGROUND ATMOSPHERE --- */}
      {/* 1. The Weather Effect (Rain & Lightning) */}
      <WeatherEffect
        className="fixed inset-0 z-0 bg-slate-950" 
        rainIntensity={100}
        rainSpeed={0.2}
        lightningEnabled={true}
        lightningFrequency={5}
        thunderEnabled={true}
      />
      
      {/* 2. Semi-transparent Overlay (Ensures text is readable over the rain) */}
      <div className="fixed inset-0 z-0 pointer-events-none" />

      {/* --- CONTENT CONTAINER --- */}
      <div className="relative z-10 container mx-auto px-4 py-24">
        
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1 rounded-full bg-cyan-950/60 border border-cyan-500/30 backdrop-blur-md">
            <Zap className="w-4 h-4 text-cyan-400 animate-pulse" />
            <span className="text-xs font-bold tracking-[0.2em] text-cyan-300 uppercase">System Identity</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight drop-shadow-2xl">
            ABOUT <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-blue-500 text-glow">THUNDER FOX</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed drop-shadow-md">
            A young and dynamic force driven by <span className="text-white font-bold">innovation</span>, <span className="text-white font-bold">technology</span>, and client <span className="text-white font-bold">victory</span>.
          </p>
        </motion.div>

        {/* STORY SECTION */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <div className="glass-panel rounded-3xl p-8 md:p-14 relative overflow-hidden group">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <Rocket className="w-8 h-8 text-yellow-400" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">OUR ORIGIN STORY</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-12 text-lg text-slate-200 leading-relaxed font-light">
                <p>
                  <strong className="text-cyan-400">Thunder Fox Web Solutions</strong> emerged from a passion to revolutionize the digital landscape for startups and SMEs. Founded with a vision to make cutting-edge technology accessible, we've grown into a tactical partner for businesses seeking lightning-fast digital transformation.
                </p>
                <p>
                  As an <strong className="text-purple-400">MSME-registered</strong> entity based in Cuddalore, Tamil Nadu, we bring strict compliance and enterprise-grade professionalism to every project. We don't just build websites; we engineer digital infrastructure that stands the test of time.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* MISSION & VISION GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          {[
            {
              title: "OUR MISSION",
              icon: Target,
              color: "text-red-500",
              text: "To empower startups and SMEs with reliable, lightning-fast digital solutions that drive growth. We strive to democratize advanced web technology, helping businesses of all sizes compete in the digital arena."
            },
            {
              title: "OUR VISION",
              icon: Eye,
              color: "text-cyan-500",
              text: "To become the most trusted name in software development across India. We envision a future where every business has access to world-class digital solutions, known for our innovation and client-first approach."
            }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: idx === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="electric-card glass-panel rounded-3xl p-10 group bg-slate-900/40"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-black tracking-wide text-white">{item.title}</h3>
                <div className={`p-3 rounded-xl bg-slate-900/80 border border-white/10 ${item.color} group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-8 h-8" />
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed text-lg group-hover:text-white transition-colors">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CERTIFICATIONS */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="relative glass-panel rounded-3xl border border-cyan-500/30 p-12 text-center overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50" />
             
             <h2 className="text-3xl font-bold mb-10 text-white">CERTIFICATIONS & COMPLIANCE</h2>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 rounded-2xl bg-slate-900/60 border border-white/5 hover:border-yellow-500/50 transition-colors group">
                  <Award className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-white mb-2">MSME REGISTERED</h3>
                  <p className="text-sm text-slate-400 uppercase tracking-wider">Govt. Recognized Enterprise</p>
                </div>
                
                <div className="p-6 rounded-2xl bg-slate-900/60 border border-white/5 hover:border-cyan-500/50 transition-colors group">
                  <Zap className="w-12 h-12 text-cyan-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold text-white mb-2">SPEED CERTIFIED</h3>
                  <p className="text-sm text-slate-400 uppercase tracking-wider">High-Performance Deployment</p>
                </div>
             </div>
          </div>
        </motion.section>

        {/* CORE VALUES */}
        <section>
          <motion.h2 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-3xl font-bold text-center mb-12 flex items-center justify-center gap-3 drop-shadow-lg"
          >
            <Shield className="w-6 h-6 text-purple-500" />
            CORE PROTOCOLS
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "VELOCITY", desc: "Lightning-fast delivery without compromising quality.", icon: Zap, color: "text-yellow-400" },
              { title: "TRUST", desc: "Building long-term alliances through reliability.", icon: Shield, color: "text-blue-400" },
              { title: "PRECISION", desc: "Excellence in every line of code we write.", icon: CheckCircle, color: "text-green-400" },
              { title: "INNOVATION", desc: "Staying ahead with cutting-edge technology.", icon: Rocket, color: "text-pink-400" },
            ].map((value, idx) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative bg-slate-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-6 hover:bg-slate-800/80 transition-colors"
              >
                <div className={`mb-4 ${value.color}`}>
                  <value.icon className="w-8 h-8 group-hover:animate-bounce" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 tracking-wide">{value.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {value.desc}
                </p>
                <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-transparent via-current to-transparent w-0 group-hover:w-full transition-all duration-500 ${value.color}`} />
              </motion.div>
            ))}
          </div>
        </section>

      </div>
    </main>
  );
};

export default About;